import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  m as motion,
  MAnimatePresence as AnimatePresence,
} from "../../../shared/components/Motion";
import { ButtonComponent } from "shared/components";
import { useAdmin } from "../AdminProvider";
import { modalVariants, childVariants, cld } from "shared/utils";
import "../styles/EditCertificatesModal.css";

const PRESET_LOGOS = [
  { label: "TCS", id: "tcs_logo_tictpi" },
  { label: "Lionbridge", id: "lionbridge_logo_y9njjl" },
  { label: "ClaySys", id: "claysys_logo_cbsnpp" },
];

const EMPLOYMENT_TYPES = [
  "Full-time",
  "Part-time",
  "Internship",
  "Contract",
  "Freelance",
];

const EditQualificationModal = ({
  isOpen,
  onClose,
  entryData,
  onSave,
  tabIndex,
  entryIndex,
  publishedData,
}) => {
  const { qualificationDraft, saveQualificationDraft } = useAdmin();
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    startDate: "",
    endDate: "",
    isPresent: false,
    logo: "",
    employmentType: "Full-time",
    achievements: [],
    techStack: [],
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Dynamic inputs for achievements & techStack
  const [newAchievementText, setNewAchievementText] = useState("");
  const [editingAchievementIndex, setEditingAchievementIndex] = useState(null);
  const [isAddingAchievement, setIsAddingAchievement] = useState(false);

  const [newTechText, setNewTechText] = useState("");
  const [editingTechIndex, setEditingTechIndex] = useState(null);
  const [isAddingTech, setIsAddingTech] = useState(false);

  // Parse subtitle "Company - Location" into separate fields
  const parseSubtitle = (subtitle) => {
    if (!subtitle) return { company: "", location: "" };
    const parts = subtitle.split(" - ");
    if (parts.length >= 2) {
      return {
        company: parts[0].trim(),
        location: parts.slice(1).join(" - ").trim(),
      };
    }
    return { company: subtitle.trim(), location: "" };
  };

  // Parse calendar string to extract dates
  const parseCalendarString = (calendarStr) => {
    if (!calendarStr) return { startDate: "", endDate: "", isPresent: false };
    const parts = calendarStr.split(" - ");
    const startDateStr = parts[0]?.trim() || "";
    const endDateStr = parts[1]?.trim() || "";
    const isPresent = endDateStr.toLowerCase() === "present";

    const parseMonth = (monthStr) => {
      const months = {
        jan: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        sep: "09",
        oct: "10",
        nov: "11",
        dec: "12",
      };
      const monthParts = monthStr.split(/\s+/);
      if (monthParts.length === 2) {
        const month = months[monthParts[0].toLowerCase()];
        const year = monthParts[1];
        return month && year ? `${year}-${month}` : "";
      }
      return "";
    };

    return {
      startDate: parseMonth(startDateStr),
      endDate: isPresent ? "" : parseMonth(endDateStr),
      isPresent: isPresent,
    };
  };

  useEffect(() => {
    if (isOpen) {
      if (entryData) {
        const parsed = parseCalendarString(entryData.calendar || "");
        const subtitleParsed = parseSubtitle(entryData.subtitle || "");

        setFormData({
          title: entryData.title || "",
          company: entryData.company || subtitleParsed.company,
          location: entryData.location || subtitleParsed.location,
          startDate: parsed.startDate,
          endDate: parsed.endDate,
          isPresent: parsed.isPresent,
          logo: entryData.logo || "",
          employmentType: entryData.employmentType || "Full-time",
          achievements: Array.isArray(entryData.achievements)
            ? [...entryData.achievements]
            : [],
          techStack: Array.isArray(entryData.techStack)
            ? [...entryData.techStack]
            : [],
        });
      } else {
        setFormData({
          title: "",
          company: "",
          location: "",
          startDate: "",
          endDate: "",
          isPresent: false,
          logo: "",
          employmentType: "Full-time",
          achievements: [],
          techStack: [],
        });
      }
      setNewAchievementText("");
      setEditingAchievementIndex(null);
      setIsAddingAchievement(false);
      setNewTechText("");
      setEditingTechIndex(null);
      setIsAddingTech(false);
      setErrors({});
    }
  }, [isOpen, entryData]);

  // Convert "2025-01" to "JAN 2025"
  const formatMonthYear = (dateStr) => {
    if (!dateStr) return "";
    const [year, month] = dateStr.split("-");
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${months[monthIndex]} ${year}`;
  };

  const buildCalendarString = () => {
    const startFormatted = formatMonthYear(formData.startDate);
    const endFormatted = formData.isPresent
      ? "Present"
      : formatMonthYear(formData.endDate);

    return `${startFormatted} - ${endFormatted}`;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.company.trim())
      newErrors.company = "Company/Institute is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.isPresent && !formData.endDate) {
      newErrors.endDate = "End date is required (or mark as Present)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const baseDraft = qualificationDraft || publishedData || {};
      const tabs = baseDraft.tabs ? [...baseDraft.tabs] : [];

      if (!tabs[tabIndex]) {
        throw new Error("Tab not found");
      }

      const updatedTab = { ...tabs[tabIndex] };
      const updatedData = [...(updatedTab.data || [])];
      const calendarValue = buildCalendarString();

      let subtitle = formData.company.trim();
      if (formData.location.trim()) {
        subtitle += ` - ${formData.location.trim()}`;
      }

      const entryPayload = {
        title: formData.title.trim(),
        company: formData.company.trim(),
        location: formData.location.trim(),
        subtitle: subtitle,
        calendar: calendarValue,
        logo: formData.logo.trim(),
        employmentType: formData.employmentType || "Full-time",
        achievements: formData.achievements.filter((a) => a.trim().length > 0),
        techStack: formData.techStack.filter((t) => t.trim().length > 0),
      };

      if (entryIndex !== null && entryIndex !== undefined && entryIndex >= 0) {
        updatedData[entryIndex] = entryPayload;
      } else {
        updatedData.unshift(entryPayload);
      }

      updatedTab.data = updatedData;
      tabs[tabIndex] = updatedTab;

      const updatedQualification = {
        ...baseDraft,
        tabs: tabs,
      };

      await saveQualificationDraft(updatedQualification);
      onSave();
      onClose();
    } catch (error) {
      console.error("Error saving qualification entry:", error);
      alert("Failed to save entry. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: null });
  };

  // Achievements handlers
  const handleAddAchievement = () => {
    if (!newAchievementText.trim()) return;
    setFormData({
      ...formData,
      achievements: [...formData.achievements, newAchievementText.trim()],
    });
    setNewAchievementText("");
    setIsAddingAchievement(false);
  };

  const handleSaveEditAchievement = () => {
    if (!newAchievementText.trim()) return;
    const updated = [...formData.achievements];
    updated[editingAchievementIndex] = newAchievementText.trim();
    setFormData({ ...formData, achievements: updated });
    setNewAchievementText("");
    setEditingAchievementIndex(null);
  };

  const handleDeleteAchievement = (index) => {
    const updated = formData.achievements.filter((_, i) => i !== index);
    setFormData({ ...formData, achievements: updated });
  };

  // Tech stack handlers
  const handleAddTech = () => {
    if (!newTechText.trim()) return;
    setFormData({
      ...formData,
      techStack: [...formData.techStack, newTechText.trim()],
    });
    setNewTechText("");
    setIsAddingTech(false);
  };

  const handleSaveEditTech = () => {
    if (!newTechText.trim()) return;
    const updated = [...formData.techStack];
    updated[editingTechIndex] = newTechText.trim();
    setFormData({ ...formData, techStack: updated });
    setNewTechText("");
    setEditingTechIndex(null);
  };

  const handleDeleteTech = (index) => {
    const updated = formData.techStack.filter((_, i) => i !== index);
    setFormData({ ...formData, techStack: updated });
  };

  const isNewEntry =
    entryIndex === null || entryIndex === undefined || entryIndex < 0;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "680px",
              maxHeight: "90vh",
              overflowY: "auto",
              width: "90%",
            }}
          >
            <i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
            ></i>

            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
            >
              {isNewEntry
                ? "Add Qualification Entry"
                : "Edit Qualification Entry"}
            </motion.h3>

            {/* Title Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "0.75rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{ fontWeight: "600", fontSize: "small" }}
              >
                Title *
              </label>
              <input
                className="glass__card"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Software Engineer, System Engineer"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {errors.title && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.7rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {errors.title}
                </span>
              )}
            </motion.div>

            {/* Company & Employment Type in 2 columns */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 0.8fr",
                gap: "0.75rem",
                marginBottom: "0.75rem",
              }}
            >
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{ position: "relative" }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{ fontWeight: "600", fontSize: "small" }}
                >
                  Company / Institute *
                </label>
                <input
                  className="glass__card"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="e.g., Tata Consultancy Services"
                  style={{
                    width: "100%",
                    height: "2.5rem",
                    marginTop: "0.5rem",
                    padding: "0 1rem",
                    border: "2px solid var(--title-color)",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    backgroundColor: "var(--body-color)",
                    color: "var(--border-color)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {errors.company && (
                  <span
                    style={{
                      color: "#ef4444",
                      fontSize: "0.7rem",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {errors.company}
                  </span>
                )}
              </motion.div>

              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{ position: "relative" }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{ fontWeight: "600", fontSize: "small" }}
                >
                  Employment Type
                </label>
                <select
                  className="glass__card"
                  name="employmentType"
                  value={formData.employmentType}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    height: "2.5rem",
                    marginTop: "0.5rem",
                    padding: "0 0.75rem",
                    border: "2px solid var(--title-color)",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    backgroundColor: "var(--body-color)",
                    color: "var(--border-color)",
                    outline: "none",
                    boxSizing: "border-box",
                    cursor: "pointer",
                  }}
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </motion.div>
            </div>

            {/* Location Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "0.75rem" }}
            >
              <label
                className="preview-modal-subtitle"
                style={{ fontWeight: "600", fontSize: "small" }}
              >
                Location *
              </label>
              <input
                className="glass__card"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Chennai, TN"
                style={{
                  width: "100%",
                  height: "2.5rem",
                  marginTop: "0.5rem",
                  padding: "0 1rem",
                  border: "2px solid var(--title-color)",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  backgroundColor: "var(--body-color)",
                  color: "var(--border-color)",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {errors.location && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "0.7rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {errors.location}
                </span>
              )}
            </motion.div>

            {/* Company Logo (Cloudinary ID) with Quick Presets */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "0.75rem" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{ fontWeight: "600", fontSize: "small" }}
                >
                  Company Logo (Cloudinary ID)
                </label>
                <div style={{ display: "flex", gap: "0.35rem" }}>
                  {PRESET_LOGOS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, logo: p.id })
                      }
                      className="glass__button"
                      style={{
                        background:
                          formData.logo === p.id
                            ? "var(--title-color)"
                            : "color-mix(in srgb, var(--title-color) 10%, var(--body-color))",
                        color:
                          formData.logo === p.id
                            ? "var(--body-color)"
                            : "var(--title-color)",
                        border: "1px solid var(--title-color)",
                        borderRadius: "0.25rem",
                        padding: "0.15rem 0.4rem",
                        fontSize: "0.65rem",
                        cursor: "pointer",
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                  marginTop: "0.5rem",
                }}
              >
                <input
                  className="glass__card"
                  type="text"
                  name="logo"
                  value={formData.logo}
                  onChange={handleInputChange}
                  placeholder="e.g., tcs_logo_tictpi, lionbridge_logo_y9njjl, claysys_logo_cbsnpp"
                  style={{
                    flex: 1,
                    height: "2.5rem",
                    padding: "0 1rem",
                    border: "2px solid var(--title-color)",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    backgroundColor: "var(--body-color)",
                    color: "var(--border-color)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {formData.logo ? (
                  <img
                    src={cld(formData.logo, 80)}
                    alt="Logo preview"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "0.35rem",
                      objectFit: "contain",
                      border: "1px solid var(--title-color)",
                      padding: "2px",
                      background: "#fff",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "0.35rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "color-mix(in srgb, var(--title-color) 10%, var(--body-color))",
                      color: "var(--text-color)",
                      fontSize: "1.2rem",
                    }}
                  >
                    <i className="bx bx-buildings"></i>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Date Range: Start Date & End Date */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              {/* Start Date */}
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{ position: "relative" }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{ fontWeight: "600", fontSize: "small" }}
                >
                  Start Date *
                </label>
                <input
                  className="glass__card"
                  type="month"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    height: "2.5rem",
                    padding: "0 0.75rem",
                    marginTop: "0.5rem",
                    border: "2px solid var(--title-color)",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    backgroundColor: "var(--body-color)",
                    color: "var(--border-color)",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {errors.startDate && (
                  <span
                    style={{
                      color: "#ef4444",
                      fontSize: "0.7rem",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {errors.startDate}
                  </span>
                )}
              </motion.div>

              {/* End Date */}
              <motion.div
                variants={childVariants}
                initial="hidden"
                animate="visible"
                style={{ position: "relative" }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{ fontWeight: "600", fontSize: "small" }}
                >
                  End Date {!formData.isPresent && "*"}
                </label>
                <input
                  className="glass__card"
                  type="month"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  disabled={formData.isPresent}
                  style={{
                    width: "100%",
                    marginTop: "0.5rem",
                    height: "2.5rem",
                    padding: "0 0.75rem",
                    border: "2px solid var(--title-color)",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    backgroundColor: formData.isPresent
                      ? "lightgray"
                      : "var(--body-color)",
                    color: "var(--border-color)",
                    outline: "none",
                    boxSizing: "border-box",
                    opacity: formData.isPresent ? 0.5 : 1,
                    cursor: formData.isPresent ? "not-allowed" : "pointer",
                  }}
                />
                {errors.endDate && (
                  <span
                    style={{
                      color: "#ef4444",
                      fontSize: "0.7rem",
                      marginTop: "0.25rem",
                      display: "block",
                    }}
                  >
                    {errors.endDate}
                  </span>
                )}
              </motion.div>
            </div>

            {/* Present Checkbox */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{ position: "relative", marginBottom: "1rem" }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "var(--text-color)",
                }}
              >
                <input
                  className="glass__card"
                  type="checkbox"
                  name="isPresent"
                  checked={formData.isPresent}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      isPresent: e.target.checked,
                    });
                    if (errors.endDate) setErrors({ ...errors, endDate: null });
                  }}
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                />
                Currently Working Here (Present)
              </label>
            </motion.div>

            {/* Key Achievements List */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              className="glass__card"
              style={{
                position: "relative",
                marginBottom: "1rem",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid color-mix(in srgb, var(--title-color) 20%, var(--body-color))",
                background:
                  "color-mix(in srgb, var(--title-color) 3%, var(--body-color))",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{
                    fontWeight: "600",
                    fontSize: "small",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <i className="bx bx-star"></i> Key Achievements (
                  {formData.achievements.length})
                </label>
                {!isAddingAchievement &&
                  editingAchievementIndex === null && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingAchievement(true);
                        setNewAchievementText("");
                      }}
                      className="glass__button"
                      style={{
                        background: "var(--title-color)",
                        color: "var(--body-color)",
                        border: "none",
                        borderRadius: "0.25rem",
                        padding: "0.2rem 0.5rem",
                        fontSize: "0.7rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.2rem",
                      }}
                    >
                      <i className="bx bx-plus"></i> Add Bullet
                    </button>
                  )}
              </div>

              {/* Existing Achievements */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.4rem",
                }}
              >
                {formData.achievements.map((ach, idx) => (
                  <div
                    key={idx}
                    className="glass__card"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                      padding: "0.4rem 0.6rem",
                      background: "var(--body-color)",
                      borderRadius: "0.35rem",
                      border: "1px solid color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
                    }}
                  >
                    {editingAchievementIndex === idx ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.35rem",
                          width: "100%",
                        }}
                      >
                        <textarea
                          rows={2}
                          value={newAchievementText}
                          onChange={(e) =>
                            setNewAchievementText(e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "0.4rem",
                            borderRadius: "0.25rem",
                            border: "1px solid var(--title-color)",
                            fontSize: "0.8rem",
                            background: "var(--body-color)",
                            color: "var(--title-color)",
                            outline: "none",
                            resize: "vertical",
                            boxSizing: "border-box",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            gap: "0.4rem",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setEditingAchievementIndex(null)
                            }
                            style={{
                              background: "transparent",
                              border: "1px solid var(--title-color)",
                              borderRadius: "0.25rem",
                              padding: "0.15rem 0.4rem",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                              color: "var(--title-color)",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveEditAchievement}
                            style={{
                              background: "var(--title-color)",
                              color: "var(--body-color)",
                              border: "none",
                              borderRadius: "0.25rem",
                              padding: "0.15rem 0.5rem",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span
                          style={{
                            fontSize: "0.8rem",
                            lineHeight: 1.4,
                            color: "var(--text-color)",
                            flex: 1,
                          }}
                        >
                          • {ach}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.25rem",
                            flexShrink: 0,
                          }}
                        >
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setEditingAchievementIndex(idx);
                              setNewAchievementText(ach);
                              setIsAddingAchievement(false);
                            }}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              padding: 0,
                              background: "var(--title-color)",
                            }}
                            title="Edit achievement"
                          >
                            <i
                              className="bx bx-pencil"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                          <motion.button
                            type="button"
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteAchievement(idx)}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              padding: 0,
                              background: "var(--title-color)",
                            }}
                            title="Delete achievement"
                          >
                            <i
                              className="bx bx-trash"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {formData.achievements.length === 0 &&
                  !isAddingAchievement && (
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-color)",
                        opacity: 0.7,
                        fontStyle: "italic",
                      }}
                    >
                      No achievements added yet. Click "+ Add Bullet" to add.
                    </span>
                  )}

                {/* Add New Achievement Box */}
                {isAddingAchievement && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.35rem",
                      padding: "0.5rem",
                      borderRadius: "0.35rem",
                      background: "var(--body-color)",
                      border: "1px dashed var(--title-color)",
                    }}
                  >
                    <textarea
                      rows={2}
                      placeholder="Enter achievement bullet (e.g., Developed microservices with .NET and React)..."
                      value={newAchievementText}
                      onChange={(e) =>
                        setNewAchievementText(e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "0.4rem",
                        borderRadius: "0.25rem",
                        border: "1px solid var(--title-color)",
                        fontSize: "0.8rem",
                        background: "var(--body-color)",
                        color: "var(--title-color)",
                        outline: "none",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "0.4rem",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingAchievement(false);
                          setNewAchievementText("");
                        }}
                        style={{
                          background: "transparent",
                          border: "1px solid var(--title-color)",
                          borderRadius: "0.25rem",
                          padding: "0.15rem 0.4rem",
                          fontSize: "0.7rem",
                          cursor: "pointer",
                          color: "var(--title-color)",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddAchievement}
                        style={{
                          background: "var(--title-color)",
                          color: "var(--body-color)",
                          border: "none",
                          borderRadius: "0.25rem",
                          padding: "0.15rem 0.5rem",
                          fontSize: "0.7rem",
                          cursor: "pointer",
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Tech Stack Chips Input */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              className="glass__card"
              style={{
                position: "relative",
                marginBottom: "1.25rem",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                border: "1px solid color-mix(in srgb, var(--title-color) 20%, var(--body-color))",
                background:
                  "color-mix(in srgb, var(--title-color) 3%, var(--body-color))",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <label
                  className="preview-modal-subtitle"
                  style={{
                    fontWeight: "600",
                    fontSize: "small",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <i className="bx bx-code-alt"></i> Tech Stack (
                  {formData.techStack.length})
                </label>
                {!isAddingTech && editingTechIndex === null && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingTech(true);
                      setNewTechText("");
                    }}
                    className="glass__button"
                    style={{
                      background: "var(--title-color)",
                      color: "var(--body-color)",
                      border: "none",
                      borderRadius: "0.25rem",
                      padding: "0.2rem 0.5rem",
                      fontSize: "0.7rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.2rem",
                    }}
                  >
                    <i className="bx bx-plus"></i> Add Tech
                  </button>
                )}
              </div>

              {/* Chips container */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.35rem",
                  alignItems: "center",
                }}
              >
                {formData.techStack.map((tech, idx) => (
                  <span
                    key={idx}
                    className="glass__card qualification__techstack-chip"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.35rem",
                      padding: "0.2rem 0.5rem",
                      border: "1px solid var(--title-color)",
                      borderRadius: "0.35rem",
                      background: "var(--body-color)",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      color: "var(--title-color)",
                    }}
                  >
                    {editingTechIndex === idx ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.2rem",
                        }}
                      >
                        <input
                          type="text"
                          value={newTechText}
                          onChange={(e) => setNewTechText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEditTech();
                          }}
                          style={{
                            width: "80px",
                            fontSize: "0.75rem",
                            border: "1px solid var(--title-color)",
                            borderRadius: "0.2rem",
                            padding: "0.1rem 0.3rem",
                            outline: "none",
                            background: "var(--body-color)",
                            color: "var(--title-color)",
                          }}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={handleSaveEditTech}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "#10b981",
                            fontSize: "0.9rem",
                            padding: 0,
                          }}
                        >
                          <i className="bx bx-check"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingTechIndex(null)}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "#ef4444",
                            fontSize: "0.9rem",
                            padding: 0,
                          }}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </span>
                    ) : (
                      <>
                        <span
                          onClick={() => {
                            setEditingTechIndex(idx);
                            setNewTechText(tech);
                            setIsAddingTech(false);
                          }}
                          style={{ cursor: "pointer" }}
                          title="Click to edit"
                        >
                          {tech}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteTech(idx)}
                          style={{
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            color: "var(--text-color)",
                            fontSize: "0.9rem",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Remove"
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      </>
                    )}
                  </span>
                ))}

                {formData.techStack.length === 0 && !isAddingTech && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-color)",
                      opacity: 0.7,
                      fontStyle: "italic",
                    }}
                  >
                    No tech stack items added. Click "+ Add Tech" to add tags.
                  </span>
                )}

                {/* Add new tech input */}
                {isAddingTech && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="e.g., React"
                      value={newTechText}
                      onChange={(e) => setNewTechText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTech();
                        }
                      }}
                      style={{
                        height: "1.8rem",
                        padding: "0 0.4rem",
                        borderRadius: "0.3rem",
                        border: "1px solid var(--title-color)",
                        fontSize: "0.75rem",
                        background: "var(--body-color)",
                        color: "var(--title-color)",
                        outline: "none",
                        width: "110px",
                      }}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddTech}
                      style={{
                        background: "var(--title-color)",
                        color: "var(--body-color)",
                        border: "none",
                        borderRadius: "0.2rem",
                        padding: "0.2rem 0.4rem",
                        fontSize: "0.7rem",
                        cursor: "pointer",
                      }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingTech(false);
                        setNewTechText("");
                      }}
                      style={{
                        background: "transparent",
                        color: "var(--title-color)",
                        border: "1px solid var(--title-color)",
                        borderRadius: "0.2rem",
                        padding: "0.2rem 0.4rem",
                        fontSize: "0.7rem",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </span>
                )}
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={childVariants}
              initial="hidden"
              animate="visible"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <ButtonComponent
                buttonTitle="Cancel"
                buttonText="Cancel"
                onClick={onClose}
              />
              <ButtonComponent
                buttonTitle="Save Entry"
                buttonText={saving ? "Saving..." : "Save Entry"}
                onClick={handleSave}
                disabled={saving}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditQualificationModal;
